import styled from "@emotion/styled";
import {
  Button,
  Grid2,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { socialLinks, TAbout } from "../../utils/about";

// Keyframes for fade-in-up animation
const fadeInUp = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Name = styled(Typography)({
  paddingTop: 4,
  paddingBottom: 4,
  fontWeight: "bold",
  animation: "fadeInUp 0.5s ease-out forwards",
  animationDelay: "0.2s",
  opacity: 0,
});

const AnimatedTypography = styled(Typography)({
  animation: "fadeInUp 0.5s ease-out forwards",
  animationDelay: "0.4s",
  opacity: 0,
});


const AnimatedBox = styled(Box)({
  animation: "fadeInUp 0.5s ease-out forwards",
  animationDelay: "0.6s",
  opacity: 0,
});

const GlobalStyles = styled.div`
  ${fadeInUp}

  .social-icon {
    transition: transform 0.2s ease, color 0.2s ease;
  }

  .social-icon:hover {
    transform: translateY(-3px);
  }
`;

type AboutProps = {
  about: TAbout;
};

const About: React.FC<AboutProps> = ({ about }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <GlobalStyles>
      <Grid2
        id="Home"
        container
        spacing={isMobile ? 4 : 6}
        justifyContent={"center"}
      >
        <Grid2
          offset={{ md: 2 }}
          size={{ xs: 12, md: 6 }}
          justifyContent={"center"}
          display={"flex"}
          flexDirection={"column"}
          sx={{
            textAlign: { xs: "start", md: "left" },
            order: { xs: 2, md: 1 },
          }}
        >
          <Name variant="h3">{about.name}</Name>

          <AnimatedTypography variant="body1">
            {about.content}
          </AnimatedTypography>

          <Box>
            <AnimatedBox>
              <Button
                href="/resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  boxShadow: "none",
                  backgroundColor: "#536493",
                  mt: 2,
                  transition: "transform 0.2s ease, background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#647aa8",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                variant="contained"
              >
                Resume
              </Button>
            </AnimatedBox>
          </Box>

          <AnimatedBox
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "start",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {socialLinks.map((link) => (
                <Tooltip key={link.name} title={link.name}>
                  <IconButton
                    className="social-icon"
                    aria-label={link.name}
                    href={link.url}
                    target="_blank"
                    sx={{
                      padding: 0,
                      color: theme.palette.text.primary,
                      "&:hover": {
                        color: theme.palette.info.main,
                      },
                    }}
                  >
                    {link.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </AnimatedBox>
        </Grid2>
        <Grid2
          size={{ xs: 12, md: 4 }}
          sx={{
            order: { xs: 1, md: 2 },
            display: "flex",
            justifyContent: "center",
          }}
        />
      </Grid2>
    </GlobalStyles>
  );
};

export default About;
